import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { Button } from "../../../components/ui/button.tsx";
import { Input } from "../../../components/ui/input.tsx";
import { Label } from "../../../components/ui/label.tsx";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/pwaConfig.ts";
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Edit2, Users, Upload, Download, Building2, Phone, User, MapPin, FileText, Loader2, Globe2 } from "lucide-react";
import { Loader } from "../../../components/Loader";
import { COUNTRY_OPTIONS, INDIA_STATES_AND_UT, isIndiaCountry } from "../../../constants/countryStates";

interface Customer {
  id: number;
  companyName: string;
  companyMobile: string;
  personName: string;
  personMobile: string;
  address: string;
  country: string;
  state: string;
  pincode?: string;
  gst?: string;
  createdAt?: string;
}

const emptyForm = {
  id: 0,
  companyName: "",
  companyMobile: "",
  personName: "",
  personMobile: "",
  address: "",
  country: "India",
  state: "",
  pincode: "",
  gst: "",
};

const normalizeHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const columnAliases: Record<string, keyof typeof emptyForm> = {
  companyname: "companyName",
  companymobile: "companyMobile",
  personname: "personName",
  personmobile: "personMobile",
  address: "address",
  country: "country",
  state: "state",
  pincode: "pincode",
  gst: "gst",
};

export const CustomersPage = () => {
  const { authToken = "", id = "" } = useSelector((state: any) => state.loginData || {});
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCustomers, setTotalCustomers] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [importing, setImporting] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const searchTermRef = useRef("");

  const maxResults = 10;

  const fetchCustomers = useCallback(async (pg = 1, search = "") => {
    if (!id) return;
    setLoading(true);
    try {
      const firstResults = (pg - 1) * maxResults;
      const response = await axios.get(`${API_BASE_URL}account/${id}/customers`, {
        params: { firstResults, maxResults, search },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCustomers(response.data.customers || []);
      setTotalCustomers(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [authToken, id]);

  useEffect(() => {
    fetchCustomers(page, searchTermRef.current);
  }, [fetchCustomers, page]);

  useEffect(() => {
    searchTermRef.current = searchTerm;
    const timer = setTimeout(() => {
      setPage(1);
      fetchCustomers(1, searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [fetchCustomers, searchTerm]);

  const resetForm = () => setFormData({ ...emptyForm });

  const openDrawerForCreate = () => {
    resetForm();
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const openDrawerForEdit = (customer: Customer) => {
    setFormData({
      id: customer.id,
      companyName: customer.companyName,
      companyMobile: customer.companyMobile,
      personName: customer.personName,
      personMobile: customer.personMobile,
      address: customer.address,
      country: customer.country || "India",
      state: customer.state,
      pincode: customer.pincode || "",
      gst: customer.gst || "",
    });
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isCreating) {
        await axios.post(
          `${API_BASE_URL}account/${id}/customer/create`,
          {
            companyName: formData.companyName,
            companyMobile: formData.companyMobile,
            personName: formData.personName,
            personMobile: formData.personMobile,
            address: formData.address,
            country: formData.country || "India",
            state: formData.state,
            pincode: formData.pincode || null,
            gst: formData.gst?.trim() ? formData.gst : null,
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast.success("Customer created successfully");
      } else {
        await axios.put(
          `${API_BASE_URL}account/${id}/customer/update`,
          {
            id: formData.id,
            companyName: formData.companyName,
            companyMobile: formData.companyMobile,
            personName: formData.personName,
            personMobile: formData.personMobile,
            address: formData.address,
            country: formData.country || "India",
            state: formData.state,
            pincode: formData.pincode || null,
            gst: formData.gst?.trim() ? formData.gst : null,
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast.success("Customer updated successfully");
      }
      setIsDrawerOpen(false);
      resetForm();
      fetchCustomers(page, searchTerm);
    } catch (error) {
      toast.error(isCreating ? "Failed to create customer" : "Failed to update customer");
      console.error("Failed to submit customer:", error);
    }
  };

  const handleDelete = async (customerId: number) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      await axios.delete(`${API_BASE_URL}account/${id}/customer/${customerId}/delete`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success("Customer deleted");
      fetchCustomers(page, searchTerm);
    } catch (error) {
      toast.error("Failed to delete customer");
      console.error("Failed to delete customer:", error);
    }
  };

  const mapImportedRows = (rows: any[][]) => {
    if (!rows.length) {
      throw new Error("The selected file is empty.");
    }

    const [headerRow, ...dataRows] = rows;
    const mappedHeaders = headerRow.map((cell) => columnAliases[normalizeHeader(String(cell || ""))] || null);
    const requiredHeaders = ["companyName", "companyMobile", "personName", "personMobile", "address", "state"];
    const missingHeaders = requiredHeaders.filter((header) => !mappedHeaders.includes(header as keyof typeof emptyForm));

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(", ")}`);
    }

    return dataRows
      .filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""))
      .map((row) => {
        const customer = { ...emptyForm };
        mappedHeaders.forEach((field, index) => {
          if (!field) return;
          const value = row[index];
          (customer as Record<string, string | number>)[field] = value == null ? "" : String(value).trim();
        });
        if (!customer.country) customer.country = "India";
        return customer;
      })
      .filter((customer) => customer.companyName && customer.companyMobile && customer.personName && customer.personMobile && customer.address && customer.state)
      .map(({ id: _id, ...customer }) => customer);
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        blankrows: false,
        defval: "",
      }) as any[][];

      const importedCustomers = mapImportedRows(rows);
      if (!importedCustomers.length) {
        throw new Error("No valid customer rows found in the file.");
      }

      const response = await axios.post(
        `${API_BASE_URL}account/${id}/customer/bulk-create`,
        { customers: importedCustomers },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const created = response.data.created || 0;
      const failed = response.data.failed || 0;
      if (created > 0) {
        toast.success(`${created} customers imported successfully`);
      }
      if (failed > 0) {
        toast.error(`${failed} rows could not be imported`);
      }
      setPage(1);
      fetchCustomers(1, searchTerm);
    } catch (error: any) {
      toast.error(error?.message || "Failed to import customers");
      console.error("Customer import failed:", error);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExportCustomers = async () => {
    if (!id) return;
    setExporting(true);
    try {
      const response = await axios.get(`${API_BASE_URL}account/${id}/customers`, {
        params: { firstResults: 0, maxResults: 10000, search: searchTerm },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const list: Customer[] = response.data.customers || [];
      const headers = ["companyName", "companyMobile", "personName", "personMobile", "address", "country", "state", "pincode", "gst"];
      const rows = list.map((c) => [
        c.companyName,
        c.companyMobile,
        c.personName,
        c.personMobile,
        c.address,
        c.country || "India",
        c.state,
        c.pincode || "",
        c.gst || "",
      ]);
      const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customers_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(list.length ? `Exported ${list.length} customers` : "Exported (no rows)");
    } catch (e) {
      console.error(e);
      toast.error("Failed to export customers");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(totalCustomers / maxResults);

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      {/* ── Hero Banner ───────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-100 shadow-sm">
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-200/20 rounded-full -translate-y-24 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-purple-200/20 rounded-full translate-y-20 -translate-x-16" />
        
        <div className="relative p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-gray-900">Customers</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl">Manage your customer directory, import bulk records from Excel or CSV, and maintain complete customer information.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <div className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
              {totalCustomers} Total
            </div>
            <div className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">
              Page {page} of {totalPages || 1}
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Buttons ────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <a
            href="/customer-import-sample.csv"
            download
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition shadow-sm"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Sample CSV</span>
            <span className="sm:hidden">Sample</span>
          </a>
          <Button
            type="button"
            variant="outline"
            onClick={handleExportCustomers}
            disabled={exporting}
            className="flex items-center gap-1.5 font-semibold border-gray-300 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-800"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{exporting ? "Exporting…" : "Export CSV"}</span>
            <span className="sm:hidden text-xs">{exporting ? "…" : "Export"}</span>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1.5 font-semibold border-gray-300 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">{importing ? "Importing..." : "Import"}</span>
            <span className="sm:hidden text-xs">{importing ? "…" : "CSV"}</span>
          </Button>
        </div>
        <Button onClick={openDrawerForCreate} className="btn-primary flex items-center gap-2 font-semibold w-full sm:w-auto justify-center">
          <Plus className="w-5 h-5" />
          New Customer
        </Button>
      </div>

      {/* ── Info Box ───────────────────────────────────────────── */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 flex items-start gap-3">
        <FileText className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-800"><span className="font-bold">Import Guide:</span> Use the sample file headers exactly as provided. Upload CSV from Excel or Excel workbook (.xlsx) with the same columns.</p>
      </div>

      {/* ── Search Bar ─────────────────────────────────────────── */}
      <div className="relative">
        <Search className="absolute left-4 top-3.5 text-gray-400 w-5 h-5" />
        <Input
          placeholder="Search by company, person name, or mobile..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-12 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        />
      </div>

      {/* ── Desktop Table ──────────────────────────────────────── */}
      <div className="hidden md:block bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {customers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-300" />
            </div>
            <p className="font-semibold text-lg mb-1">No customers found</p>
            <p className="text-sm">Start by importing or creating your first customer.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-75">
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-600">#</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-600">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      Company
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-600">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-purple-600" />
                      Company Phone
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-600">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-green-600" />
                      Contact Person
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-600">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-orange-600" />
                      Person Phone
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-600">
                    <div className="flex items-center gap-2">
                      <Globe2 className="w-4 h-4 text-cyan-600" />
                      Country
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-red-600" />
                      State
                    </div>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-widest text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, idx) => (
                  <tr key={customer.id} className="border-b border-gray-75 hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-4 text-xs text-gray-400 font-bold">{(page - 1) * maxResults + idx + 1}</td>
                    <td className="px-6 py-4"><span className="font-bold text-gray-900">{customer.companyName}</span></td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-xs">{customer.companyMobile}</td>
                    <td className="px-6 py-4 text-gray-700 font-medium">{customer.personName}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-xs">{customer.personMobile}</td>
                    <td className="px-6 py-4 text-gray-600">{customer.country || "India"}</td>
                    <td className="px-6 py-4 text-gray-600">{customer.state || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDrawerForEdit(customer)}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50 font-semibold text-xs"
                        >
                          <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50 font-semibold text-xs"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Mobile Card View ───────────────────────────────────── */}
      <div className="md:hidden space-y-4">
        {customers.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-xl border border-gray-200">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-300" />
            </div>
            <p className="font-semibold text-lg">No customers found</p>
            <p className="text-sm mt-1">Start by importing or creating your first customer.</p>
          </div>
        ) : (
          customers.map((customer) => (
            <div key={customer.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-lg truncate">{customer.companyName}</p>
                    <p className="text-sm text-gray-500 truncate">{customer.personName}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {[
                  { icon: Building2, label: "Company", value: customer.companyMobile, color: "#3b82f6" },
                  { icon: User, label: "Contact", value: customer.personMobile, color: "#10b981" },
                  customer.country && { icon: Globe2, label: "Country", value: customer.country, color: "#06b6d4" },
                  customer.state && { icon: MapPin, label: "State", value: customer.state, color: "#ef4444" },
                ].filter(Boolean).map((item: any) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <item.icon className="w-4 h-4" style={{ color: item.color }} />
                    <span className="text-xs text-gray-500">{item.label}:</span>
                    <span className="text-sm font-mono text-gray-900">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={() => openDrawerForEdit(customer)} className="flex-1 btn-primary text-xs font-semibold">
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(customer.id)}
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50 text-xs font-semibold"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 shadow-sm">
          <div className="text-sm text-gray-700 font-semibold">
            <span className="text-blue-600">Page {page}</span> of <span className="text-blue-600">{totalPages}</span>
            <span className="text-gray-500 ml-3">({totalCustomers} total customers)</span>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setPage(Math.max(1, page - 1))} 
              disabled={page === 1} 
              className="flex items-center gap-1.5 font-semibold border-blue-300 hover:bg-white disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button 
              onClick={() => setPage(Math.min(totalPages, page + 1))} 
              disabled={page === totalPages} 
              className="btn-primary flex items-center gap-1.5 font-semibold disabled:opacity-40"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {isDrawerOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsDrawerOpen(false)} />}

      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transition-all duration-300 w-full md:w-96 max-h-screen md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          isDrawerOpen ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        <div className="p-6 border-b border-gray-100 sticky top-0 bg-gradient-to-r from-blue-50 to-indigo-50 flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <div>
            <h2 className="text-xl font-black text-gray-900">{isCreating ? "Create New Customer" : "Edit Customer"}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{isCreating ? "Add a new customer to your directory" : "Update customer information"}</p>
          </div>
          <button onClick={() => setIsDrawerOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl rounded-lg hover:bg-white/50 w-8 h-8 flex items-center justify-center">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 pb-20 md:pb-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-gray-700 block mb-2">Company Name *</Label>
              <Input value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} placeholder="Company name" required />
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">Company Mobile *</Label>
              <Input value={formData.companyMobile} onChange={(e) => setFormData({ ...formData, companyMobile: e.target.value })} placeholder="Company mobile number" required />
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">Person Name *</Label>
              <Input value={formData.personName} onChange={(e) => setFormData({ ...formData, personName: e.target.value })} placeholder="Contact person name" required />
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">Person Mobile *</Label>
              <Input value={formData.personMobile} onChange={(e) => setFormData({ ...formData, personMobile: e.target.value })} placeholder="Person mobile number" required />
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">Country *</Label>
              <select
                required
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={formData.country}
                onChange={(e) => {
                  const country = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    country,
                    gst: isIndiaCountry(country) ? prev.gst : "",
                  }));
                }}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">State / UT *</Label>
              {isIndiaCountry(formData.country) ? (
                <select
                  required
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                >
                  <option value="">Select state / UT</option>
                  {INDIA_STATES_AND_UT.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State or province"
                  required
                />
              )}
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">Address *</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Address" required />
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">Pincode <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} placeholder="Pincode" />
            </div>
            {isIndiaCountry(formData.country) ? (
              <div>
                <Label className="text-gray-700 block mb-2">
                  GSTIN <span className="text-gray-400 font-normal">(recommended for India)</span>
                </Label>
                <Input value={formData.gst} onChange={(e) => setFormData({ ...formData, gst: e.target.value })} placeholder="15-character GSTIN" />
              </div>
            ) : null}
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Button type="submit" className="flex-1 btn-primary">{isCreating ? "Create Customer" : "Update Customer"}</Button>
            <Button type="button" variant="outline" onClick={() => setIsDrawerOpen(false)} className="flex-1">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomersPage;
